import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Alert } from '../ui/Alert';
import { Sheet, SheetBody, SheetContent, SheetFooter, SheetTrigger } from '../ui/Sheet';
import { apiFetch } from '../../lib/api';
import { ApiError, type Template } from '../../types/api';

/**
 * "Save as template": stores the campaign's current content (what is on
 * screen, saved or not) as a new template. The template is a copy: later
 * edits to either side do not change the other. A block campaign becomes a
 * block template, an HTML campaign an HTML template.
 */
export function SaveAsTemplateSheet({
  defaultName,
  content,
}: {
  defaultName: string;
  /** What to store: the block model with its html, or only html. */
  content: { html: string; blockJson?: unknown };
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch<{ template: Template }>('/templates', {
        method: 'POST',
        body: { name: name.trim(), ...content },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['templates'] }),
  });

  const error =
    mutation.error instanceof ApiError && mutation.error.body.message
      ? mutation.error.body.message
      : mutation.isError
        ? 'The template could not be saved. Check the content for errors.'
        : null;

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setName(defaultName);
          mutation.reset();
        }
      }}
    >
      <SheetTrigger asChild>
        <button type="button" className="pf-btn pf-btn-sm">
          Save as template
        </button>
      </SheetTrigger>
      <SheetContent
        title="Save as template"
        description="Stores a copy of this campaign's content as a new template. The campaign and the template stay independent."
      >
        <form
          className="flex min-h-0 flex-grow flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          <SheetBody>
            {mutation.isSuccess ? (
              <div role="status" className="text-sm text-success-text">
                Saved as a template.{' '}
                <Link to={`/skabeloner/${mutation.data.template.id}`}>Open the template</Link>
              </div>
            ) : null}
            {error ? <Alert>{error}</Alert> : null}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="as-template-name">Template name</Label>
              <Input
                id="as-template-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </SheetBody>
          <SheetFooter>
            <Button type="submit" disabled={mutation.isPending || name.trim() === ''}>
              {mutation.isPending ? 'Saving…' : 'Save template'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
